
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { formSchema, FormValues } from "./schema";
import { useState, useEffect } from "react";
import { Form } from "@/components/ui/form";
import { PersonalInfoFields } from "./components/PersonalInfoFields";
import { AddressFields } from "./components/AddressFields";
import { TermsField } from "./components/TermsField";
import { toast } from "@/components/ui/use-toast";
import { saveMembership } from "@/lib/supabase/membership";
import { useNavigate } from "react-router-dom";
import { PaymentSummaryCard } from "./components/PaymentSummaryCard";
import { FormSubmitButton } from "./components/FormSubmitButton";
import { EmailConfirmationMessage } from "./components/EmailConfirmationMessage";

type MembershipFormProps = {
  initialMembershipType: "active" | "benefactor" | "donor";
  donationAmount?: number;
};

export function MembershipForm({ initialMembershipType, donationAmount = 0 }: MembershipFormProps) {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [currentMembershipType, setCurrentMembershipType] = useState<"active" | "benefactor" | "donor">(initialMembershipType);

  // Set default values based on the membership type
  const defaultValues: FormValues = {
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    postalCode: "",
    membershipType: initialMembershipType,
    additionalDonation: initialMembershipType === "donor" ? (donationAmount || 0) : 0,
    // For donor type, acceptTerms is not required, so set to true by default
    acceptTerms: initialMembershipType === "donor" ? true : false,
    acceptNewsletter: false,
    // For donor type, declareFrenchCitizen is not required
    declareFrenchCitizen: initialMembershipType === "donor" ? true : false,
    // Add a field to track whether to include the entry fee for benefactor members
    includeEntryFee: false
  };

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues
  });

  // Use an effect to update the form values when initialMembershipType changes
  useEffect(() => {
    // Clear validation errors when switching membership types
    form.clearErrors();
    
    setCurrentMembershipType(initialMembershipType);
    form.setValue("membershipType", initialMembershipType);
    
    // Update acceptTerms and declareFrenchCitizen based on membership type
    if (initialMembershipType === "donor") {
      form.setValue("additionalDonation", donationAmount || 0);
      form.setValue("acceptTerms", true);
      form.setValue("declareFrenchCitizen", true);
    } else {
      // Reset to defaults for non-donor types
      form.setValue("additionalDonation", 0);
      form.setValue("acceptTerms", false);
      form.setValue("declareFrenchCitizen", false);
    }
    
    console.log("EFFECT: MembershipForm updated membershipType to:", initialMembershipType);
  }, [initialMembershipType, donationAmount, form]);

  console.log("DEBUG MembershipForm - Initial type:", initialMembershipType);
  console.log("DEBUG MembershipForm - Form values:", form.getValues());

  async function onSubmit(values: FormValues) {
    setIsSubmitting(true);

    try {
      // Pour tous les types de membres, utiliser le processus de vérification par email
      const membershipResponse = await saveMembership({
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        phone: values.phone,
        address: values.address || "",
        city: values.city || "",
        postalCode: values.postalCode || "",
        membershipType: values.membershipType,
        additionalDonation: initialMembershipType === "donor" ? (donationAmount || 0) : values.additionalDonation,
        includeEntryFee: values.includeEntryFee,
        acceptNewsletter: values.acceptNewsletter
      });

      if (membershipResponse.success) {
        setEmailSent(true);
        toast({
          title: initialMembershipType === "donor" ? "Demande de don envoyée !" : "Demande d'adhésion envoyée !",
          description: "Veuillez vérifier votre email pour confirmer.",
        });
      }
    } catch (error) {
      console.error("Erreur lors de la soumission du formulaire:", error);
      toast({
        title: "Erreur",
        description: "Une erreur est survenue lors de la soumission du formulaire.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  if (emailSent) {
    return <EmailConfirmationMessage membershipType={initialMembershipType} />;
  }

  const formTitle = initialMembershipType === "donor"
    ? "Formulaire de don"
    : initialMembershipType === "active"
      ? "Formulaire d'adhésion - Membre Actif"
      : "Formulaire d'adhésion - Membre Bienfaiteur";

  return (
    <div className="bg-white p-6 rounded-xl shadow-md animate-fadeIn">
      <h2 className="text-2xl font-semibold text-center mb-6 text-[#00123A]">
        {formTitle}
      </h2>
      
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-6">
              <PersonalInfoFields form={form} />
              <AddressFields form={form} />
            </div>
            
            <div className="space-y-6">
              <PaymentSummaryCard 
                membershipType={initialMembershipType}
                form={form}
                donationAmount={donationAmount}
              />
              
              <TermsField form={form} />
            </div>
          </div>
          
          <div className="flex justify-center mt-6">
            <FormSubmitButton 
              isSubmitting={isSubmitting}
              membershipType={initialMembershipType}
              donationAmount={donationAmount}
            />
          </div>
        </form>
      </Form>
    </div>
  );
}
